import AgentExplainerLayout, { Section, PointList } from "@/components/marketing/AgentExplainerLayout";
import { AgentHeroVisual, VoiceCallFlow } from "@/components/marketing/AgentFlowDiagram";
import { Link } from "react-router-dom";

export default function VoiceAgents() {
  return (
    <AgentExplainerLayout
      active="voice"
      testId="voice-agents-page"
      eyebrow="Agents · Voice"
      title="Voice Agents that follow a script—without open conversation."
      lead={(n) =>
        `Voice Agents play prompts, capture DTMF or short speech, and branch the call. They are the lowest-cost automation layer in ${n}: predictable, fast to design, and ideal when every caller should hear the same path.`
      }
      heroVisual={<AgentHeroVisual variant="voice" />}
    >
      <Section eyebrow="What they are" title="Scripted telephony, not a freeform chat" testId="voice-what">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-start">
          <div className="space-y-4 text-muted-foreground">
            <p>
              A Voice Agent is a campaign flow built from play-audio, collect-info, menus, and branches.
              The caller hears recorded or TTS audio, presses keys (or says a short phrase), and the flow
              decides the next node—schedule a callback, transfer, or hang up.
            </p>
            <p>
              There is no live reasoning model on the line. That keeps latency low and spend tied to
              audio/TTS minutes instead of a full duplex AI session.
            </p>
            <PointList
              items={[
                { title: "Deterministic", body: "Same greeting, same menu, same outcomes for every lead." },
                { title: "Designer-first", body: "Drag nodes in the campaign builder—no code required to change a prompt." },
                { title: "SIP-native", body: "Runs through your VICIdial / FreeSWITCH path like any other campaign." },
              ]}
            />
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-background to-muted/40 p-6 shadow-md">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/20 blur-2xl" />
            <p className="disp relative text-lg font-bold">Best when…</p>
            <ul className="relative mt-4 space-y-2 text-sm text-muted-foreground">
              <li>• Appointment reminders and confirmations</li>
              <li>• Simple surveys and opt-in captures</li>
              <li>• After-hours IVR that filters before a human queue</li>
              <li>• High-volume dials where every second of AI would be waste</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section eyebrow="Call flow" title="How a Voice Agent call moves" testId="voice-flow">
        <p className="mb-6 max-w-2xl text-muted-foreground">
          Dial, answer, play, capture, branch—then exit. Each step is a node you can inspect in CDR and live dashboards.
        </p>
        <VoiceCallFlow />
        <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Originate", "Campaign prefix hits the engine; media stream opens."],
            ["Prompt", "TTS or uploaded audio plays the scripted line."],
            ["Input", "DTMF digits or a short speech capture fill variables."],
            ["Route", "Branches send the call to transfer, another prompt, or hangup."],
          ].map(([t, b], i) => (
            <li key={t} className="rounded-xl border border-border bg-card p-4">
              <p className="font-mono text-[11px] text-primary">0{i + 1}</p>
              <p className="disp mt-1 font-semibold">{t}</p>
              <p className="mt-1 text-sm text-muted-foreground">{b}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section eyebrow="Cost" title="Why Voice Agents are cost-effective" testId="voice-cost">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-md">
            <p className="text-sm font-semibold">Relative spend per answered minute</p>
            <div className="mt-5 space-y-4">
              {[
                { label: "Voice Agent", w: "28%", note: "TTS / audio + telephony" },
                { label: "AI Agent (live)", w: "72%", note: "Duplex model + voice" },
                { label: "Human agent", w: "100%", note: "Wage + seat + after-call" },
              ].map((row) => (
                <div key={row.label}>
                  <div className="mb-1 flex justify-between text-xs">
                    <span className="font-medium">{row.label}</span>
                    <span className="text-muted-foreground">{row.note}</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-full bg-muted">
                    <div className="agent-bar h-full rounded-full bg-primary/70" style={{ width: row.w }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Illustrative mix—your rates depend on assigned TTS/STT and carrier minutes.
            </p>
          </div>
          <PointList
            items={[
              { title: "No live LLM session", body: "You are not paying for continuous model turns on every greeting." },
              { title: "Reuse prompts", body: "One audio asset serves thousands of dials until you replace it." },
              { title: "Filter before spend", body: "Drop voicemail and bad numbers cheaply before escalating." },
              { title: "Predictable invoices", body: "Minute math stays close to dial volume × audio length." },
            ]}
          />
        </div>
      </Section>

      <Section eyebrow="Limits" title="Where Voice Agents stop being enough" testId="voice-limits">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
            <p className="disp font-semibold text-amber-900 dark:text-amber-100">Limitations</p>
            <PointList
              items={[
                "Cannot hold a natural back-and-forth conversation",
                "Unexpected answers often fall through to a generic branch",
                "Long branching trees become hard to maintain",
                "Accent / noisy lines hurt DTMF-only and short STT captures",
                "Feels robotic if every prompt is a flat recording",
              ]}
            />
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <p className="disp font-semibold">When to graduate</p>
            <p className="mt-2 text-sm text-muted-foreground">
              If leads ask open questions, negotiate, or need qualification before a human—
              move those branches to an{" "}
              <Link to="/agents/ai" className="font-semibold text-primary hover:underline">AI Agent</Link>
              {" "}or wrap both in a{" "}
              <Link to="/agents/hybrid" className="font-semibold text-primary hover:underline">Hybrid</Link>
              {" "}flow so only warm callers pay for intelligence.
            </p>
          </div>
        </div>
      </Section>
    </AgentExplainerLayout>
  );
}
